import React, { useMemo, useState } from 'react';

import { IconButton, Typography } from '@mui/material';
import {
  AlineButton,
  AlineTooltip,
  panelHeaderHeightPx,
  PlainIconButton,
  Spacer,
} from 'src/components';
import { AddIcon } from 'src/components/icons/AddIcon';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { EmptyCommentsIcon } from 'src/components/icons/EmptyCommentsIcon';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { useSortCommentsByDocPosition } from 'src/hooks/comment';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import { CommentBox } from './CommentBox';
import type { CommentPanelComment } from './CommentsDrawer';
import { CommentsFilter, CommentsFilterMenu, useCommentsFilter } from './CommentsFilterMenu';
import { useScrollCommentBoxIntoView } from './use-scroll-comment-box-into-view';
import { useBackToAI } from '../BackToAIButton';

const useStyles = makeStyles()(theme => ({
  backButton: {
    margin: theme.spacing(1.5, 1, 1.5, 0),
  },
  button: {
    marginTop: theme.spacing(3),
  },
  empty: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100% - ${panelHeaderHeightPx}px)`,
    padding: theme.spacing(7, 2),
  },
  emptyIcon: {
    marginBottom: theme.spacing(2),
  },
  headerBar: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `solid 1px ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    height: panelHeaderHeightPx,
    padding: theme.spacing(1, 1.5),
  },
  panelContainer: {
    backgroundColor: theme.appColors.background,
    height: `calc(100% - ${panelHeaderHeightPx}px)`,
    overflowY: 'auto',
    width: '100%',
  },
}));

interface ViewCommentsPanelProps {
  comments: CommentPanelComment[];
}

export function ViewCommentsPanel({ comments }: ViewCommentsPanelProps): JSX.Element {
  const { classes } = useStyles();
  const {
    comments: { setActivePanel },
  } = useEditorTabStateActions();
  const { viewing } = useEditingMode();
  const commentsFilter = useCommentsFilter();
  const { editor, sortedComments } = useSortCommentsByDocPosition(comments);
  const handleBackToAI = useBackToAI();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const filteredComments = useMemo(() => {
    return sortedComments.filter(comment => {
      switch (commentsFilter) {
        case CommentsFilter.All:
          return true;
        case CommentsFilter.Resolved:
          return comment.resolved;
        case CommentsFilter.Unresolved:
          return !comment.resolved;
      }
    });
  }, [commentsFilter, sortedComments]);
  useScrollCommentBoxIntoView(filteredComments);

  if (!filteredComments.length)
    return (
      <>
        <div className={classes.headerBar}>
          <AlineTooltip
            arrow
            open={tooltipOpen}
            placement="top"
            title="Back to AI"
            onClose={() => setTooltipOpen(false)}
            onOpen={() => setTooltipOpen(true)}
          >
            <IconButton size="small" onClick={handleBackToAI}>
              <BackArrowIcon />
            </IconButton>
          </AlineTooltip>
          <CommentsFilterMenu />
          <Spacer />
          <AlineTooltip arrow placement="bottom" title="New comment">
            <div className={classes.backButton}>
              <PlainIconButton
                Icon={AddIcon}
                aria-label="New comment"
                disabled={viewing || !editor?.selection}
                onClick={(): void => {
                  setActivePanel('AddComment');
                }}
              />
            </div>
          </AlineTooltip>
        </div>
        <div className={classes.empty}>
          <EmptyCommentsIcon classes={{ root: classes.emptyIcon }} />
          <Typography variant="subtitle2">No comments</Typography>
          <AlineButton
            fullWidth
            className={classes.button}
            color="primary"
            disabled={viewing}
            variant="contained"
            onClick={(): void => {
              setActivePanel('AddComment');
            }}
          >
            Add Comment
          </AlineButton>
        </div>
      </>
    );

  return (
    <>
      <div className={classes.headerBar}>
        <AlineTooltip
          arrow
          open={tooltipOpen}
          placement="top"
          title="Back to AI"
          onClose={() => setTooltipOpen(false)}
          onOpen={() => setTooltipOpen(true)}
        >
          <IconButton size="small" onClick={handleBackToAI}>
            <BackArrowIcon />
          </IconButton>
        </AlineTooltip>
        <CommentsFilterMenu />
        <Spacer />
        <AlineTooltip arrow placement="bottom" title="New comment">
          <div className={classes.backButton}>
            <PlainIconButton
              Icon={AddIcon}
              aria-label="New comment"
              disabled={viewing || !editor?.selection}
              onClick={(): void => {
                setActivePanel('AddComment');
              }}
            />
          </div>
        </AlineTooltip>
      </div>
      <div className={classes.panelContainer}>
        {filteredComments.map((comment): JSX.Element => {
          return (
            <CommentBox
              key={comment.id}
              showReplies
              commentOrReply={comment}
              isRootCommentResolved={comment.resolved}
              rootCommentId={comment.id}
            />
          );
        })}
      </div>
    </>
  );
}
